import { useEffect, useState } from "react";
import api, { fmtDateTime } from "../lib/api";
import Topbar from "../components/Topbar";
import { useRoleLabel } from "../lib/auth";
import { useAuth } from "../lib/auth";
import {
  UserCog, Mail, CheckCircle2, XCircle, Building2, Layers, Pencil, X,
  KeyRound, UserX, UserCheck, Plus, Copy, History, Search, Download, AtSign,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const ROLE_COLORS = {
  corporate_admin: "bg-purple-100 text-purple-800",
  branch_manager: "bg-blue-100 text-blue-800",
  treasury: "bg-emerald-100 text-emerald-800",
  supplier: "bg-amber-100 text-amber-800",
  legal: "bg-rose-100 text-rose-800",
  sistemas: "bg-cyan-100 text-cyan-800",
};

const AUDIT_COLORS = {
  created: "bg-emerald-100 text-emerald-800",
  updated: "bg-blue-100 text-blue-800",
  deactivated: "bg-red-100 text-red-800",
  reactivated: "bg-emerald-100 text-emerald-800",
  password_reset: "bg-amber-100 text-amber-800",
  department_changed: "bg-indigo-100 text-indigo-800",
  email_changed: "bg-violet-100 text-violet-800",
};

// Fix WH82 (2026-10-16): "mantenimiento" existe en el backend (deps.VALID_ROLES y
// UserCreateRequest/UserUpdateRequest) pero faltaba en esta lista, así que no se
// podía asignar ni se veía al editar (pedido de Cristian).
const ROLES = ["corporate_admin", "branch_manager", "treasury", "legal", "operativo", "hr_admin", "colaborador", "direccion", "mantenimiento", "sistemas"];
const emptyForm = { email: "", full_name: "", role: "branch_manager", branch_id: "", branches_authorized: [], can_create_payments: false, department: "", allowed_cost_centers: [], password: "", two_fa_exempt: false };

export default function Users() {
  const { t } = useTranslation();
  const { user: me } = useAuth();
  const roleLabel = useRoleLabel();
  const [list, setList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchesById, setBranchesById] = useState({});
  const [costCenters, setCostCenters] = useState([]);
  const [view, setView] = useState("users");
  const [audit, setAudit] = useState([]);
  const [auditAction, setAuditAction] = useState("all");
  const [auditSearch, setAuditSearch] = useState("");

  const [formMode, setFormMode] = useState(null); // "create" | "edit"
  const [form, setForm] = useState(emptyForm);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);

  const [pwReveal, setPwReveal] = useState(null); // {email, password}
  const [resetTarget, setResetTarget] = useState(null);
  const [resetReason, setResetReason] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [deactTarget, setDeactTarget] = useState(null);
  const [deactReason, setDeactReason] = useState("");
  // Fix WH82 (2026-10-16): cambiar el correo de acceso de una cuenta.
  const [emailTarget, setEmailTarget] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailReason, setEmailReason] = useState("");

  const loadUsers = () => api.get("/users").then((r) => setList(r.data));
  const loadAudit = () => api.get("/users/audit-log").then((r) => setAudit(r.data));

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/branches"), api.get("/cost-centers")]).then(([u, b, c]) => {
      setList(u.data);
      setBranches(b.data);
      const m = {};
      for (const br of b.data) m[br.id] = br;
      setBranchesById(m);
      setCostCenters(c.data);
    });
  }, []);

  useEffect(() => { if (view === "audit") loadAudit(); }, [view]);

  const branchLabel = (id) => {
    const b = branchesById[id];
    return b ? (b.code || b.name || "—") : "—";
  };

  const openCreate = () => { setForm(emptyForm); setFormMode("create"); };
  const openEdit = (u) => {
    setEditingUser(u);
    setForm({
      email: u.email, full_name: u.full_name || "", role: u.role || "branch_manager",
      branch_id: u.branch_id || "", branches_authorized: u.branches_authorized || [],
      can_create_payments: !!u.can_create_payments, department: u.department || "",
      allowed_cost_centers: u.allowed_cost_centers || [], two_fa_exempt: !!u.two_fa_exempt,
    });
    setFormMode("edit");
  };

  const toggleInList = (key, val) => {
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  };

  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const submitForm = async () => {
    if (formMode === "create" && !emailValid(form.email)) { toast.error(t("users.invalidEmail")); return; }
    if (!form.full_name.trim()) { toast.error(t("users.nameRequired")); return; }
    if (form.role === "branch_manager" && !form.branch_id) { toast.error(t("users.branchRequired")); return; }
    if (formMode === "create" && form.password && form.password.length < 6) { toast.error(t("users.passwordMin")); return; }
    setSaving(true);
    try {
      if (formMode === "create") {
        const payload = {
          email: form.email.trim(), full_name: form.full_name.trim(), role: form.role,
          branch_id: form.role === "branch_manager" ? form.branch_id : null,
          branches_authorized: form.branches_authorized, can_create_payments: form.can_create_payments,
          department: form.department.trim() || null, allowed_cost_centers: form.allowed_cost_centers,
          password: form.password ? form.password : undefined, two_fa_exempt: form.two_fa_exempt,
        };
        const r = await api.post("/users", payload);
        setFormMode(null);
        setPwReveal({ email: r.data.user.email, password: r.data.password_initial });
        await loadUsers();
      } else {
        const payload = {
          full_name: form.full_name.trim(), role: form.role,
          branch_id: form.role === "branch_manager" ? form.branch_id : null,
          branches_authorized: form.branches_authorized, can_create_payments: form.can_create_payments,
          department: form.department.trim() || null, allowed_cost_centers: form.allowed_cost_centers,
          two_fa_exempt: form.two_fa_exempt,
        };
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success(t("users.savedChanges"));
        setFormMode(null);
        await loadUsers();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const confirmReset = async () => {
    if (resetReason.trim().length < 5) { toast.error(t("users.reasonMin")); return; }
    if (resetPassword && resetPassword.length < 6) { toast.error(t("users.passwordMin")); return; }
    setSaving(true);
    try {
      const r = await api.post(`/users/${resetTarget.id}/reset-password`, {
        reason: resetReason.trim(),
        new_password: resetPassword ? resetPassword : undefined,
      });
      setResetTarget(null); setResetReason(""); setResetPassword("");
      setPwReveal({ email: resetTarget.email, password: r.data.new_password });
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const confirmEmailChange = async () => {
    if (!emailValid(newEmail.trim())) { toast.error(t("users.invalidEmail")); return; }
    if (emailReason.trim().length < 5) { toast.error(t("users.reasonMin")); return; }
    setSaving(true);
    try {
      const r = await api.post(`/users/${emailTarget.id}/change-email`, {
        new_email: newEmail.trim(), reason: emailReason.trim(),
      });
      setEmailTarget(null); setNewEmail(""); setEmailReason("");
      setPwReveal({ email: r.data.email, password: r.data.new_password });
      const listas = r.data.old_email_in_flags || [];
      if (listas.length) toast.warning(t("users.emailInFlags", { lists: listas.join(", ") }), { duration: 15000 });
      else toast.success(t("users.emailChanged"));
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const confirmDeactivate = async () => {
    if (deactReason.trim().length < 5) { toast.error(t("users.reasonMin")); return; }
    setSaving(true);
    try {
      await api.post(`/users/${deactTarget.id}/deactivate`, { reason: deactReason.trim() });
      toast.success(t("users.deactivated"));
      setDeactTarget(null); setDeactReason("");
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally { setSaving(false); }
  };

  const reactivate = async (u) => {
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(t("users.reactivated"));
      await loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const copyPassword = () => { navigator.clipboard?.writeText(pwReveal.password); toast.success(t("users.copied")); };

  const exportCredentials = async () => {
    try {
      const res = await api.get("/admin/credentials-export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "credenciales_chizy_chiz.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("users.credentialsDownloaded"));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) toast.error(t("users.credentialsAlreadyDelivered"));
      else if (status === 404) toast.error(t("users.noCredentials"));
      else toast.error(t("common.error"));
    }
  };

  const sortedUsers = [...list].sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1));
  const filteredAudit = audit.filter((a) => {
    if (auditAction !== "all" && a.action !== auditAction) return false;
    if (auditSearch && !`${a.target_user_email} ${a.actor_user_email}`.toLowerCase().includes(auditSearch.toLowerCase())) return false;
    return true;
  });

  return (
    <>
      <Topbar title={t("users.title")} subtitle={t("users.subtitle", { count: list.length })} />
      <div className="p-6 pb-28 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl p-1">
            <button data-testid="users-tab-users" onClick={() => setView("users")} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${view === "users" ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}>{t("users.tabUsers")}</button>
            <button data-testid="users-tab-audit" onClick={() => setView("audit")} className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition flex items-center gap-1.5 ${view === "audit" ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}><History className="w-3.5 h-3.5" /> {t("users.tabAudit")}</button>
          </div>
          {view === "users" && (
            <div className="flex items-center gap-2">
              <button data-testid="users-export-credentials-btn" onClick={exportCredentials} className="px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50">
                <Download className="w-4 h-4" /> {t("users.exportCredentials")}
              </button>
              <button data-testid="users-new-btn" onClick={openCreate} className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" strokeWidth={2.5} /> {t("users.newUser")}
              </button>
            </div>
          )}
        </div>

        {view === "users" ? (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.user")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.email")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.role")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.branchesAuthorized")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.department")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.allowedCostCenters")}</th>
                  <th className="text-center px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.canPay")}</th>
                  <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.status")}</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${u.active ? "" : "opacity-50"}`}>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center font-bold text-sm">{u.full_name?.[0] || "U"}</div>
                        <div>
                          <div className="text-sm font-semibold">{u.full_name}</div>
                          {u.must_change_password && <div className="text-[10px] font-bold text-amber-700 bg-amber-50 inline-block px-1.5 py-0.5 rounded">{t("users.firstLoginPending")}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-sm text-slate-600"><span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" />{u.email}</span></td>
                    <td className="px-6 py-3.5"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[u.role] || "bg-slate-100"}`}>{roleLabel(u.role)}</span></td>
                    <td className="px-6 py-3.5">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(u.branches_authorized || []).length === 0 ? <span className="text-xs text-slate-400">—</span> : (u.branches_authorized || []).map((bid) => (
                          <span key={bid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700"><Building2 className="w-3 h-3" />{branchLabel(bid)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-sm text-slate-700">{u.department || <span className="text-xs text-slate-400">—</span>}</td>
                    <td className="px-6 py-3.5">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {(u.allowed_cost_centers || []).length === 0 ? <span className="text-xs text-slate-400">—</span> : (u.allowed_cost_centers || []).map((code) => (
                          <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700"><Layers className="w-3 h-3" />{code}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-center">
                      {u.can_create_payments ? <CheckCircle2 data-testid={`user-canpay-yes-${u.email}`} className="w-5 h-5 text-emerald-600 inline" /> : <XCircle data-testid={`user-canpay-no-${u.email}`} className="w-5 h-5 text-slate-300 inline" />}
                    </td>
                    <td className="px-6 py-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${u.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{u.active ? t("users.active") : t("users.inactive")}</span>
                    </td>
                    <td className="px-6 py-3.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        <button data-testid={`user-edit-${u.email}`} onClick={() => openEdit(u)} title={t("common.edit")} className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 border border-slate-200"><Pencil className="w-3.5 h-3.5" /></button>
                        <button data-testid={`user-reset-${u.email}`} onClick={() => { setResetTarget(u); setResetReason(""); setResetPassword(""); }} title={t("users.resetPassword")} className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 border border-amber-200"><KeyRound className="w-3.5 h-3.5" /></button>
                        {u.role !== "supplier" && u.id !== me?.id && (!u.is_olimpo_superadmin || me?.is_olimpo_superadmin) && (
                          <button data-testid={`user-email-${u.email}`} onClick={() => { setEmailTarget(u); setNewEmail(""); setEmailReason(""); }} title={t("users.changeEmail")} className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50 border border-violet-200"><AtSign className="w-3.5 h-3.5" /></button>
                        )}
                        {u.active ? (
                          <button data-testid={`user-deactivate-${u.email}`} disabled={u.id === me?.id} onClick={() => { setDeactTarget(u); setDeactReason(""); }} title={t("users.deactivate")} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 border border-red-200 disabled:opacity-40 disabled:cursor-not-allowed"><UserX className="w-3.5 h-3.5" /></button>
                        ) : (
                          <button data-testid={`user-reactivate-${u.email}`} onClick={() => reactivate(u)} title={t("users.reactivate")} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 border border-emerald-200"><UserCheck className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-4" data-testid="users-audit-panel">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input data-testid="audit-search" type="text" value={auditSearch} onChange={(e) => setAuditSearch(e.target.value)} placeholder={t("users.auditSearchPh")} className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              </div>
              <select data-testid="audit-action-filter" value={auditAction} onChange={(e) => setAuditAction(e.target.value)} className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                <option value="all">{t("users.allActions")}</option>
                {Object.keys(AUDIT_COLORS).map((a) => <option key={a} value={a}>{t(`users.action_${a}`)}</option>)}
              </select>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("common.date")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.auditAction")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.auditTarget")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.auditActor")}</th>
                    <th className="text-left px-6 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("users.auditDetail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAudit.map((a) => (
                    <tr key={a.id} data-testid={`audit-row-${a.id}`} className="border-b border-slate-100 last:border-0 align-top">
                      <td className="px-6 py-3.5 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(a.created_at)}</td>
                      <td className="px-6 py-3.5"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${AUDIT_COLORS[a.action] || "bg-slate-100"}`}>{t(`users.action_${a.action}`)}</span></td>
                      <td className="px-6 py-3.5 text-sm text-slate-700">{a.target_user_email}</td>
                      <td className="px-6 py-3.5 text-sm text-slate-500">{a.actor_user_email}</td>
                      <td className="px-6 py-3.5 text-xs text-slate-600 max-w-md">
                        {a.reason && <div className="mb-1"><span className="font-semibold">{t("users.reason")}:</span> {a.reason}</div>}
                        {a.changes && Object.keys(a.changes).length > 0 && (
                          <ul className="space-y-0.5">
                            {Object.entries(a.changes).map(([f, v]) => (
                              <li key={f} className="font-mono text-[11px]">{f}: <span className="text-red-600">{JSON.stringify(v.from)}</span> → <span className="text-emerald-700">{JSON.stringify(v.to)}</span></li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit user modal */}
      {formMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid={formMode === "create" ? "user-create-modal" : "user-edit-modal"}>
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2"><UserCog className="w-5 h-5 text-slate-600" /><h3 className="font-display font-bold text-lg">{formMode === "create" ? t("users.newUser") : form.full_name}</h3></div>
              <button onClick={() => setFormMode(null)} data-testid="user-form-close" className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("common.email")}</label>
                  <input data-testid="user-form-email" type="email" value={form.email} disabled={formMode === "edit"} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, email: v })); }} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] disabled:bg-slate-100 disabled:text-slate-500" />
                  {formMode === "edit" && <p className="text-[10px] text-slate-400 mt-1">{t("users.emailNotEditable")}</p>}
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.fullName")}</label>
                  <input data-testid="user-form-name" type="text" value={form.full_name} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, full_name: v })); }} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("common.role")}</label>
                  <select data-testid="user-form-role" value={form.role} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, role: v })); }} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                    {/* Fix WH82: si el rol de la cuenta no está en la lista (p. ej. proveedor), se muestra el real en vez de "Corporativo" */}
                    {(ROLES.includes(form.role) ? ROLES : [form.role, ...ROLES]).map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                  </select>
                </div>
                {form.role === "branch_manager" && (
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.mainBranch")}</label>
                    <select data-testid="user-form-branch" value={form.branch_id} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, branch_id: v })); }} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                      <option value="">{t("users.chooseBranch")}</option>
                      {branches.map((b) => <option key={b.id} value={b.id}>{b.code ? `${b.code} — ${b.name}` : b.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.branchesAuthorized")}</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {branches.map((b) => {
                    const checked = form.branches_authorized.includes(b.id);
                    return (
                      <button key={b.id} type="button" data-testid={`user-form-branch-${b.id}`} onClick={() => toggleInList("branches_authorized", b.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-sm transition ${checked ? "border-[#FFDF00] bg-[#FFFBE6]" : "border-slate-200 hover:bg-slate-50"}`}>
                        {checked ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Building2 className="w-3.5 h-3.5 text-slate-400" />}{b.code || b.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input data-testid="user-form-canpay" type="checkbox" checked={form.can_create_payments} onChange={(e) => { const v = e.target.checked; setForm(prev => ({ ...prev, can_create_payments: v })); }} className="w-4 h-4 accent-[#FFDF00]" />
                {t("users.canPayLabel")}
              </label>

              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <input data-testid="user-form-2fa-exempt" type="checkbox" checked={form.two_fa_exempt} onChange={(e) => { const v = e.target.checked; setForm(prev => ({ ...prev, two_fa_exempt: v })); }} className="w-4 h-4 accent-[#FFDF00]" />
                {t("users.twoFaExemptLabel")}
              </label>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.department")}</label>
                <input data-testid="user-form-department" type="text" value={form.department} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, department: v })); }} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
              </div>

              {formMode === "create" && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.password")}</label>
                  <input data-testid="user-form-password" type="text" value={form.password} onChange={(e) => { const v = e.target.value; setForm(prev => ({ ...prev, password: v })); }} placeholder={t("users.passwordPh")} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                  <p className="text-[11px] text-slate-400 mt-1">{t("users.passwordHint")}</p>
                </div>
              )}

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.allowedCostCenters")}</label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {costCenters.map((cc) => {
                    const checked = form.allowed_cost_centers.includes(cc.code);
                    return (
                      <button key={cc.code} type="button" data-testid={`user-cc-option-${cc.code}`} onClick={() => toggleInList("allowed_cost_centers", cc.code)} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left text-sm transition ${checked ? "border-[#FFDF00] bg-[#FFFBE6]" : "border-slate-200 hover:bg-slate-50"}`}>
                        {checked ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <div className="w-4 h-4 rounded border border-slate-300 flex-shrink-0" />}
                        <span className="min-w-0"><span className="font-mono text-[11px] text-slate-400 block">{cc.code}</span><span className="truncate block">{cc.name}</span></span>
                      </button>
                    );
                  })}
                </div>
                {form.allowed_cost_centers.length === 0 && <p className="text-[11px] text-amber-700 mt-2">{t("users.noCcWarning")}</p>}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setFormMode(null)} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("common.cancel")}</button>
              <button data-testid={formMode === "create" ? "user-create-submit" : "user-edit-save"} onClick={submitForm} disabled={saving} className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm disabled:opacity-50">{saving ? t("common.saving") : (formMode === "create" ? t("users.createUser") : t("common.save"))}</button>
            </div>
          </div>
        </div>
      )}

      {/* Password reveal modal */}
      {pwReveal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-password-modal">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md">
            <div className="px-6 py-5 text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><KeyRound className="w-6 h-6 text-emerald-600" /></div>
              <h3 className="font-display font-bold text-lg">{t("users.passwordFor", { email: pwReveal.email })}</h3>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <code data-testid="user-password-value" className="flex-1 text-lg font-mono font-bold text-[#1A1A1A] break-all">{pwReveal.password}</code>
                <button data-testid="user-password-copy" onClick={copyPassword} className="p-2 rounded-lg hover:bg-slate-200 text-slate-600"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{t("users.passwordWarning")}</p>
              <button data-testid="user-password-close" onClick={() => setPwReveal(null)} className="btn-primary-chizy w-full py-2.5 rounded-xl text-sm">{t("common.understood")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-reset-modal">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md">
            <div className="px-6 py-5 space-y-4">
              <h3 className="font-display font-bold text-lg">{t("users.resetPasswordFor", { name: resetTarget.full_name })}</h3>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.newPasswordOptional")}</label>
                <input data-testid="user-reset-password" type="text" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder={t("users.newPasswordPh")} className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300" />
                <p className="text-[11px] text-slate-400 mt-1">{t("users.newPasswordHint")}</p>
              </div>
              <textarea data-testid="user-reset-reason" value={resetReason} onChange={(e) => setResetReason(e.target.value)} rows={2} placeholder={t("users.reasonPh")} className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
              <div className="flex justify-end gap-3">
                <button onClick={() => setResetTarget(null)} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("common.cancel")}</button>
                <button data-testid="user-reset-confirm" onClick={confirmReset} disabled={saving} className="px-5 py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 disabled:opacity-50">{t("users.confirmReset")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fix WH82 (2026-10-16): cambiar correo de acceso */}
      {emailTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-email-modal">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md">
            <div className="px-6 py-5 space-y-4">
              <h3 className="font-display font-bold text-lg">{t("users.changeEmailFor", { name: emailTarget.full_name })}</h3>
              <div className="text-xs text-slate-500">{t("users.currentEmail")}: <span className="font-mono text-slate-700">{emailTarget.email}</span></div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("users.newEmail")}</label>
                <input data-testid="user-email-new" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="nombre@chizychiz.com" className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
              </div>
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{t("users.changeEmailWarning")}</p>
              <textarea data-testid="user-email-reason" value={emailReason} onChange={(e) => setEmailReason(e.target.value)} rows={2} placeholder={t("users.reasonPh")} className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-300" />
              <div className="flex justify-end gap-3">
                <button onClick={() => setEmailTarget(null)} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("common.cancel")}</button>
                <button data-testid="user-email-confirm" onClick={confirmEmailChange} disabled={saving} className="px-5 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-bold hover:bg-violet-700 disabled:opacity-50">{t("users.confirmChangeEmail")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate modal */}
      {deactTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-deactivate-modal">
          <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-md">
            <div className="px-6 py-5 space-y-4">
              <h3 className="font-display font-bold text-lg">{t("users.deactivateUser", { name: deactTarget.full_name })}</h3>
              <textarea data-testid="user-deactivate-reason" value={deactReason} onChange={(e) => setDeactReason(e.target.value)} rows={3} placeholder={t("users.reasonPh")} className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-300" />
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeactTarget(null)} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("common.cancel")}</button>
                <button data-testid="user-deactivate-confirm" onClick={confirmDeactivate} disabled={saving} className="px-5 py-2.5 bg-red-500 text-white rounded-xl text-sm font-bold hover:bg-red-600 disabled:opacity-50">{t("users.deactivate")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
